import { ReactNode, useMemo } from "react";
import { useTranslation } from "react-i18next";
import useSWR from "swr";
import { Sparkle } from "@digital-twins/design-system/icons";
import {
  SocialPlatform,
  SocialTopicMetric,
  SocialTopicPostItem,
  SocialTopicSeriesQuery,
  SocialTopicSeriesSplit,
} from "@digital-twins/shared-types";

import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { SocialPlatformIcon } from "../SocialPlatformIcon";
import { SOCIAL_PLATFORM_LABELS, formatSocialCount } from "../socialFormat";
import { useSocialListeningApi } from "../SocialListeningApiContext";
import { SocialTopicSlotContext } from "../SocialTopicView";
import {
  socialTopicDayWindow,
  socialTopicFiltersCacheKey,
  UseSocialTopicFiltersResult,
} from "../useSocialTopicFilters";
import { SOCIAL_UNIVERSE_COUNTS_SWR_KEY } from "../useSocialUniverse";
import { useSocialTopicSeries } from "../useSocialTopicSeries";
import { SOCIAL_PLATFORM_TEXT_CLASS, socialPlatformColorStyle } from "./socialChartColors";
import { measureOf, percentOf, postViewsOf, SocialFilledBucket } from "./socialTopicMetrics";
import { formatBucketDate } from "./svgScale";

interface SocialPeakPopoverProps {
  context: SocialTopicSlotContext;
  /** "A" or "B", in day order. */
  letter: string;
  /** The peak's UTC day, `YYYY-MM-DD`. */
  day: string;
  /** The day's value in the current measure. */
  value: number;
  /** `value` over the daily average of the window; null when the average is 0. */
  multipleOfAverage: number | null;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  /** The focusable peak marker. */
  trigger: ReactNode;
  /** The timeline's series split, so the popover reads the chart's own request. */
  seriesSplit?: SocialTopicSeriesSplit;
}

/**
 * The series the timeline and its peaks read: every filter but the window
 * (the brush moves across the whole span), one bucket per UTC day. Both read
 * it through the same SWR key, so the popover never asks again.
 */
export function socialTimelineSeriesQuery(
  topicFilters: Pick<UseSocialTopicFiltersResult, "facetFilters">,
  split?: SocialTopicSeriesSplit,
): SocialTopicSeriesQuery {
  // "By brand" asks the same one request with the per-brand rows; "By channel" is unchanged.
  return {
    filters: topicFilters.facetFilters("date"),
    bucket: "day",
    ...(split ? { split } : {}),
  };
}

export interface SocialPeakChannelShare {
  platform: SocialPlatform;
  /** Whole percent of the day's value in the measure. */
  percent: number;
}

/**
 * The channel carrying most of a bucket in the measure; null when the bucket
 * is empty. Ties keep the bucket's channel order (SOCIAL_PLATFORMS).
 */
export function peakChannelShare(
  bucket: SocialFilledBucket | undefined,
  metric: SocialTopicMetric,
): SocialPeakChannelShare | null {
  if (!bucket) {
    return null;
  }
  const total = measureOf(metric, bucket.total.posts, bucket.total.views);
  let best: { platform: SocialPlatform; value: number } | null = null;
  for (const platform of Object.keys(bucket.platforms) as Array<SocialPlatform>) {
    const counts = bucket.platforms[platform];
    const value = measureOf(metric, counts.posts, counts.views);
    if (value > 0 && (best == null || value > best.value)) {
      best = { platform, value };
    }
  }
  const percent = best ? percentOf(best.value, total) : null;
  if (best == null || percent == null) {
    return null;
  }
  return { platform: best.platform, percent };
}

/** Stands in for the bold part of a translated sentence. */
const SLOT = "\u0000";

/** Renders `text` with the part that stood for `SLOT` in bold. */
function withBold(text: string, bold: string): ReactNode {
  const parts = text.split(SLOT);
  if (parts.length === 1) {
    return text;
  }
  return (
    <>
      {parts[0]}
      <b className="font-semibold text-foreground">{bold}</b>
      {parts.slice(1).join(bold)}
    </>
  );
}

function postAuthor(post: SocialTopicPostItem): string {
  const handle = post.authorHandle?.trim().replace(/^@+/, "") ?? "";
  if (handle) {
    return `@${handle}`;
  }
  return post.providerMeta?.displayName?.trim() || SOCIAL_PLATFORM_LABELS[post.platform];
}

function engagementSum(post: SocialTopicPostItem): number {
  const { likes, comments, shares } = post.engagement;
  return [likes, comments, shares].reduce<number>(
    (sum, value) => sum + (value != null && Number.isFinite(value) && value > 0 ? value : 0),
    0,
  );
}

/**
 * What drove a peak: the top post (it opens the post), the channel share, the
 * assistant coming state and "Show these posts" (a one-day window).
 */
export function SocialPeakPopover(props: SocialPeakPopoverProps) {
  const { context, letter, day, value, multipleOfAverage, isOpen, onOpenChange, trigger, seriesSplit } =
    props;
  const { universe, topicFilters, refreshKey, onOpenPost } = context;

  const { t, i18n } = useTranslation("app");
  const api = useSocialListeningApi();

  const metric = topicFilters.filters.metric;
  const dayFilters = useMemo(
    () => ({ ...topicFilters.facetFilters("date"), window: socialTopicDayWindow(day, day) }),
    [topicFilters, day],
  );
  // In the counts key family, so a refresh or a hide refetches it with the chart.
  const { data: topPosts } = useSWR(
    isOpen
      ? [
          SOCIAL_UNIVERSE_COUNTS_SWR_KEY,
          universe.token,
          `peak-top-post:${socialTopicFiltersCacheKey(dayFilters)}`,
          refreshKey,
        ]
      : null,
    () => api.listUniversePosts(universe.token, { filters: dayFilters, sort: "views", limit: 1 }),
    { revalidateOnFocus: false },
  );
  const seriesQuery = useMemo(
    () => socialTimelineSeriesQuery(topicFilters, seriesSplit),
    [topicFilters, seriesSplit],
  );
  const { buckets } = useSocialTopicSeries(isOpen ? universe.token : null, seriesQuery, refreshKey);

  const onOpenTopPost = (post: SocialTopicPostItem) => {
    onOpenChange(false);
    onOpenPost(post);
  };

  const onShowPosts = () => {
    onOpenChange(false);
    topicFilters.setFilter("window", socialTopicDayWindow(day, day));
  };

  const formatted = formatSocialCount(value, i18n.language);
  const multiple =
    multipleOfAverage != null
      ? multipleOfAverage.toLocaleString(i18n.language, {
          minimumFractionDigits: 1,
          maximumFractionDigits: 1,
        })
      : t("socialListening.topicPage.stats.notAvailable");
  const summary =
    metric === "reach"
      ? withBold(
          t("socialListening.topicPage.peak.summaryReach", { value: SLOT, multiple }),
          formatted,
        )
      : t("socialListening.topicPage.peak.summaryPosts", { count: value, multiple });
  const topPost = topPosts?.posts[0] ?? null;
  const topPostViews = topPost ? postViewsOf(topPost.engagement) : 0;
  const topPostText = topPost
    ? withBold(
        topPostViews > 0
          ? t("socialListening.topicPage.peak.topPostViews", {
              author: SLOT,
              value: formatSocialCount(topPostViews, i18n.language),
            })
          : t("socialListening.topicPage.peak.topPostEngagement", {
              author: SLOT,
              value: formatSocialCount(engagementSum(topPost), i18n.language),
            }),
        postAuthor(topPost),
      )
    : null;
  const share = peakChannelShare(
    buckets.find((entry) => entry.start === day),
    metric,
  );
  const shareText = share
    ? t(`socialListening.topicPage.peak.channelShare.${metric}`, {
        percent: share.percent,
        platform: SOCIAL_PLATFORM_LABELS[share.platform],
      })
    : null;

  const platformMark = (platform: SocialPlatform) => (
    <span
      aria-hidden="true"
      className="grid h-[26px] w-[26px] place-items-center rounded-full border border-border"
    >
      <span className={SOCIAL_PLATFORM_TEXT_CLASS} style={socialPlatformColorStyle(platform)}>
        <SocialPlatformIcon platform={platform} className="h-[13px] w-[13px]" />
      </span>
    </span>
  );

  return (
    <Popover open={isOpen} onOpenChange={onOpenChange}>
      {/* The marker is an SVG group, so the trigger takes it as is instead of a button. */}
      <PopoverTrigger asChild>{trigger}</PopoverTrigger>
      <PopoverContent
        align="start"
        className="w-[340px] max-w-[calc(100vw-16px)] rounded-[calc(var(--radius)+4px)] p-3 text-sm"
        data-testid={`topic-peak-popover-${letter}`}
      >
        <h4 className="mb-1.5 flex items-center gap-2.5 text-sm font-semibold">
          <span
            aria-hidden="true"
            className="grid h-[22px] w-[22px] place-items-center rounded-full bg-foreground text-xs text-background"
          >
            {letter}
          </span>
          {formatBucketDate(day, i18n.language)}
        </h4>
        <p className="mb-2" data-testid="topic-peak-summary">
          {summary}
        </p>
        {topPost && topPostText ? (
          <button
            type="button"
            data-testid="topic-peak-top-post"
            onClick={() => onOpenTopPost(topPost)}
            className="grid w-full grid-cols-[26px_1fr] items-center gap-2.5 border-t border-border py-[7px] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
          >
            {platformMark(topPost.platform)}
            <span>{topPostText}</span>
          </button>
        ) : null}
        {share && shareText ? (
          <div
            data-testid="topic-peak-channel"
            className="grid grid-cols-[26px_1fr] items-center gap-2.5 border-t border-border py-[7px]"
          >
            {platformMark(share.platform)}
            <span>{shareText}</span>
          </div>
        ) : null}
        <div className="mt-2.5 flex items-center justify-between gap-2">
          <span
            className="inline-flex items-center gap-1.5 text-xs text-muted-foreground"
            data-testid="topic-peak-assistant"
            title={t("socialListening.topicPage.peak.assistantSoon")}
          >
            <Sparkle aria-hidden="true" className="h-[13px] w-[13px] text-primary" />
            {t("socialListening.topicPage.peak.assistant")}
            <span className="sr-only">{t("socialListening.topicPage.peak.assistantSoon")}</span>
          </span>
          <Button
            type="button"
            variant="outline"
            className="h-[30px] px-3 text-xs"
            data-testid="topic-peak-show-posts"
            onClick={onShowPosts}
          >
            {t("socialListening.topicPage.peak.showPosts")}
          </Button>
        </div>
      </PopoverContent>
    </Popover>
  );
}
